import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { X, FileSpreadsheet, CheckCircle2, AlertTriangle } from "lucide-react";
import { useBackgroundJobsStore } from "@/app/store/backgroundJobs.store";
import { documentApi } from "@/features/documents/api/document.api";

// One card per active job, this is what makes background processing
// actually feel background: close the dialog that started an import, go
// do something else entirely, and this stays in the corner showing real
// progress (processedRows/totalRows straight off the job document, updated
// live by documentBulkImport.service.js's processInBackground) until it's
// done -- the same idea as Google Drive's own upload tray. Mounted once at
// the app shell level (see App.jsx), not tied to any one page, since the
// whole point is surviving navigation away from wherever the job started.
function JobCard({ job }) {
  const navigate = useNavigate();
  const dismiss = useBackgroundJobsStore((s) => s.dismiss);

  const { data } = useQuery({
    queryKey: ["documents", "bulk-import", job.id],
    queryFn: () => documentApi.getBulkImport(job.id),
    refetchInterval: (query) => (query.state.data?.status === "PROCESSING" ? 2000 : false),
  });

  if (!data) return null;

  const isProcessing = data.status === "PROCESSING";
  const percent = data.totalRows ? Math.round((data.processedRows / data.totalRows) * 100) : 0;
  const isError = data.status === "FAILED" || data.failed > 0;

  return (
    <div className="w-80 overflow-hidden rounded-xl border border-border bg-card shadow-lg">
      <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
        <div className="flex min-w-0 items-center gap-1.5 text-[14px] font-medium">
          <FileSpreadsheet className="h-4 w-4 shrink-0 text-primary" />
          <span className="truncate">{job.label}</span>
        </div>
        {!isProcessing && (
          <button onClick={() => dismiss(job.id)} className="shrink-0 text-muted-foreground hover:text-foreground" aria-label="Dismiss">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      <div className="p-3">
        {isProcessing ? (
          <>
            <p className="text-2xs text-muted-foreground">Importing {data.processedRows} of {data.totalRows} rows…</p>
            <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-primary/10">
              <div className="h-full rounded-full bg-primary transition-all duration-300" style={{ width: `${percent}%` }} />
            </div>
          </>
        ) : (
          <>
            <p className="flex items-center gap-1.5 text-2xs">
              {isError ? <AlertTriangle className="h-3.5 w-3.5 text-warning" /> : <CheckCircle2 className="h-3.5 w-3.5 text-success" />}
              {data.imported} imported, {data.skipped} skipped{data.failed ? `, ${data.failed} failed` : ""}
            </p>
            <button
              onClick={() => navigate("/documents")}
              className="mt-1.5 text-2xs font-medium text-primary hover:underline"
            >
              View details
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export function BackgroundJobsTray() {
  // Selecting s.jobs directly (not s.jobs.filter(...)) matters here -- a
  // selector that returns a freshly-filtered array every call gives
  // Zustand a new reference on every single render, which it reads as
  // "state changed," which triggers another render, which filters again...
  // an infinite loop that doesn't show up as a store bug, it shows up as
  // this whole component (and the error boundary around it) crashing on
  // every single page, since this tray is mounted globally. The actual
  // filtering happens below, outside the selector, where a new array each
  // render is completely normal and harmless.
  const allJobs = useBackgroundJobsStore((s) => s.jobs);
  const jobs = allJobs.filter((j) => !j.dismissed);
  if (!jobs.length) return null;

  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-50 flex flex-col-reverse gap-2">
      {jobs.map((job) => (
        <div key={job.id} className="pointer-events-auto">
          <JobCard job={job} />
        </div>
      ))}
    </div>
  );
}
